/**
 * Schema-management errors + visibility merging.
 *
 * `getSchemaError` converts a single AJV error into the customMessage /
 * fullPath shape used by DISPATCH_ERRORS.
 * `mergeAndHandleVisibilitySettings` merges a schema template with a config
 * and updates the field-visibility slice from the diff.
 *
 * `fetchNonActiveConfigDetails` + `checkForNonActiveConfigErrors` validate a
 * change against a non-active release-version config (used by the summary
 * screen when the user copies changes across versions).
 */

import { Fragment } from "react";
import set from "lodash/set";
import has from "lodash/has";
import { diff } from "deep-object-diff";
import { mergeAdvanced } from "object-merge-advanced";
import { getStore } from "@store";
import schemaManagementAdapter from "@features/schema-management/adapters";
import { isEmptyObject } from "@utils/object-helpers";
import {
  compareConfigs,
  setDisplayCondition,
} from "@features/sypi/_utils/sypi-interface-helpers";
import {
  currentKeysOccurrenceChangesReport,
} from "@features/schema-management/utils/schemaManagementHelpers";
import {
  showSpinner,
  stopSpinner,
} from "@actions/global";
import {
  setFieldVisibility,
} from "@features/schema-management/actions";
import { DISPATCH_ERRORS, SYPI_BRC } from "@constants/schema-management";
import { fetchSMConfig } from "./sm-fetch";
import { fetchSchema } from "./sm-fetch";
import { loadScheduledDraftConfig } from "./sm-schedule";
import { sendFinalReport } from "./sm-summary";

export const getSchemaError = (schemaError) => {
  let subNestedPath = "";
  const path = schemaError.instancePath.substring(1).replace(/\//g, ".");
  const pathKeys = path.split(".");

  if (schemaError.keyword === "additionalProperties") {
    subNestedPath = Object.values(schemaError.params)[0];
  }

  const correctPath = pathKeys.includes(subNestedPath)
    ? pathKeys.filter((pathPortion) => pathPortion !== subNestedPath).join(".")
    : path;
  const fullPath = subNestedPath
    ? `${correctPath}.${subNestedPath}`
    : correctPath;
  const configSchemaError = {
    [fullPath]: {
      ...schemaError,
      propertyKey: subNestedPath,
      customMessage: `${subNestedPath} ${[
        schemaError.message,
      ]} in ${correctPath}`,
      fullPath: fullPath,
    },
  };

  return configSchemaError;
};

export const mergeAndHandleVisibilitySettings = (
  configSchemaTemplate,
  configParam,
  setSmInitial = true,
  store = getStore()
) => {
  const mergedConfig = mergeAdvanced(configSchemaTemplate, configParam, {
    cb: (inputArg1, inputArg2, resultAboutToBeReturned) =>
      (Array.isArray(inputArg1) &&
        Array.isArray(inputArg2) &&
        inputArg2.length) ||
      typeof inputArg2 === "boolean" ||
      (typeof inputArg2 === "string" && !inputArg2)
        ? inputArg2
        : resultAboutToBeReturned,
  });
  const viewDiff = compareConfigs(configSchemaTemplate, configParam);
  if (store) {
    store.dispatch(setFieldVisibility(setDisplayCondition(viewDiff)));
  }
  return mergedConfig;
};

export const fetchNonActiveConfigDetails = async (
  version,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const changesToCopy = store.getState().schemaAllView?.changeDict;
  let config =
    store.getState().schemaConfigScheduler?.otherConfigsScheduledDrafts?.[
      version
    ];
  const checkIfsummaryChanged = (nonActiveSummaryView) => {
    let updatedNonActiveSummaryView = nonActiveSummaryView.reduce(
      (acc, cur) => {
        set(acc, cur["keyName"], cur["newKeyValue"]);
        return acc;
      },
      {}
    );
    return !!diff(changesToCopy, updatedNonActiveSummaryView);
  };
  const changeDictHasChanged = config
    ? checkIfsummaryChanged(config.summaryView)
    : true;

  let confirmedReport = {};
  if (changeDictHasChanged) {
    config = await fetchSMConfig(version);
    const { sm_config } = config;
    confirmedReport = await checkForNonActiveConfigErrors(
      version,
      sm_config,
      changesToCopy
    );

    if (confirmedReport.type !== "ALERT_DOCK") {
      await loadScheduledDraftConfig(null, {
        config: sm_config,
        release_version: version,
      });
    }
  }

  if (confirmedReport.type) {
    sendFinalReport(confirmedReport);
  }
  const success = confirmedReport.type !== "ALERT_DOCK";
  store.dispatch(stopSpinner());
  return success;
};

export const checkForNonActiveConfigErrors = async (
  release_version,
  non_active_config,
  changes_to_copy,
  store = getStore()
) => {
  const { featureName } = store.getState().schemaAllView;

  if (typeof non_active_config !== "object") {
    return {
      type: "ALERT_DOCK",
      title: "Unable to load config.",
      message: `Configuration File for release version ${release_version} has not been created yet. Visit the Import page to create a new Configuration File by importing values from another client or release version, or generate a blank Configuration File using schema default values. \n`,
    };
  }

  const { formattedSchema } = await fetchSchema(release_version);
  const {
    fullErrorReport: { schemaErrors },
  } = DISPATCH_ERRORS[featureName](
    non_active_config,
    formattedSchema,
    release_version
  );
  if (schemaErrors && schemaErrors?.length) {
    return {
      type: "ALERT_DOCK",
      title: "Schema Config Sync Error",
      message: (
        <Fragment>
          The following errors in the configuration file {release_version} have
          a conflict with schema validation: <br />
          {Object.values(schemaErrors).map((error) => (
            <b key={error}>
              {`${error}.`} <br />
            </b>
          ))}
          Please contact the application development team for assistance.
        </Fragment>
      ),
    };
  }

  const invalid_properties = [];
  Object.keys(changes_to_copy).forEach((change) => {
    if (!has(non_active_config, change)) {
      invalid_properties.push(change);
    }
  });
  if (invalid_properties && invalid_properties.length) {
    return {
      type: "ALERT_DOCK",
      title: "Schema Config Sync Error",
      message: (
        <Fragment>
          The following properties are not found in the configuration file{" "}
          {release_version}
          and copying these changes will cause a conflict with schema
          validation:
          <br />
          {invalid_properties.map((key) => (
            <b key={key}>
              {`${key}`}
              <br />
            </b>
          ))}
          Please contact the application development team for assistance.
        </Fragment>
      ),
    };
  }

  const { scheduleDraftSet } = store.getState().schemaConfigScheduler;
  const isCommercial = featureName === SYPI_BRC;
  const { configSchedules } =
    scheduleDraftSet && scheduleDraftSet.length
      ? { configSchedules: scheduleDraftSet }
      : await schemaManagementAdapter.fetchScheduledDraftList({}, isCommercial);
  const scheduledChanges = configSchedules?.filter(
    (configSchedule) =>
      configSchedule.status === "scheduled" &&
      configSchedule.release_version === release_version
  );
  let { scheduledChangesWithCommonKeys } = currentKeysOccurrenceChangesReport(
    changes_to_copy,
    scheduledChanges
  );
  if (!isEmptyObject(scheduledChangesWithCommonKeys)) {
    return {
      type: "ALERT_DOCK",
      title: "Multischeduler Error",
      message: (
        <Fragment>
          The following keys already have 3 or more changes scheduled in
          configuration file {release_version}:
          <br />
          {Object.keys(scheduledChangesWithCommonKeys).map((key) => (
            <b key={key}>
              {`${key}.`} <br />
            </b>
          ))}
        </Fragment>
      ),
    };
  }

  return {
    type: "TOAST_MESSAGE",
    message: "Summary of changes is ready to review",
  };
};
